import _ from 'lodash';
import * as pathToRegexp from 'path-to-regexp';
import menus from './index';
import { isOa } from '@/utils/index';

/**
 * @description 生成当前路由到二级导航的映射
 */
function buildPathTabNavMap(navMenu) {
  const result = {};
  navMenu.forEach((itemX) => {
    if (_.isArray(itemX.data)) {
      itemX.data.forEach((itemY) => {
        // 取菜单第三级作为TabNav展示的菜单
        const mapList = (itemY.data || []).filter((item) => !item.hide);
        recursiveSetMapList(itemY.data, mapList, result);
      });
    }
  });

  function recursiveSetMapList(menu = [], mapList, result = {}) {
    if (_.isArray(menu)) {
      menu.forEach((item) => {
        let newMapList = mapList;
        if (item.href && item.singleTabNav && newMapList.length > 1) {
          newMapList = [item];
        }
        if (item.href && !item.hideTabNav) {
          result[item.href] = newMapList;
        }
        if (_.isArray(item.data)) {
          recursiveSetMapList(item.data, newMapList, result);
        }
      });
    }
  }

  return result;
}

/**
 * @description 生成当前路由到三级导航的映射
 */
function buildPathSubNavMap(navMenu) {
  const result = {};
  navMenu.forEach((itemX) => {
    if (_.isArray(itemX.data)) {
      itemX.data.forEach((itemY) => {
        if (_.isArray(itemY.data)) {
          itemY.data.forEach((itemZ) => {
            if (_.isArray(itemZ.data)) {
              // 取菜单第四级作为TabNav展示的菜单
              const mapList = (itemZ.data || []).filter((item) => !item.hide);
              recursiveSetMapList(itemZ.data, mapList, result);
            }
          });
        }
      });
    }
  });

  function recursiveSetMapList(menu = [], mapList, result = {}) {
    if (_.isArray(menu)) {
      menu.forEach((item) => {
        let newMapList = mapList;
        if (item.href && item.singleSubNav && newMapList.length > 1) {
          newMapList = [item];
        }
        if (item.href && !item.hideSubNav) {
          result[item.href] = newMapList;
        }
      });
    }
  }

  return result;
}

/**
 * @description 生成当前路由到侧边导航的映射
 */
function buildPathMenuNavMap(navMenu) {
  const menuNav = {};
  const leafNav = {};
  navMenu.forEach((itemX) => {
    // 二级导航开始作为侧边导航
    if (_.isArray(itemX.data)) {
      const mapList = (itemX.data || []).filter((item) => !item.hide).map((item) => convertAttr(item, 2));
      recursiveSetMapList(itemX.data, mapList, 2);
    }
  });

  // Menu组件属性转换
  // Menu组件节点值包含href时，会直接修改页面地址导致页面刷新
  // 此处将href转为key，MenuNav组件中处理路由跳转
  function convertAttr(item, level) {
    const { data = [], href, iconClassName, ...rest } = item;
    let newItem = { ...rest, 'data-report-global': `menu-nav.${item.key || item.id || item.href}` };

    const siblingHasChildren = data.filter((itemSub) => !itemSub.hide).some((itemSub) => itemSub.data?.length);
    const children = data.filter((itemSub) => {
      if (itemSub.hide) {
        return false;
      }
      return level < 3 || siblingHasChildren;
    }).map((itemSub) => convertAttr(itemSub, level + 1));

    if (href) {
      newItem = { ...newItem, url: href, key: href };
    }
    if (iconClassName) {
      newItem = { ...newItem, iconClassName: `icon-menu-nav ${iconClassName}` };
    }
    if (children?.length) {
      newItem = { ...newItem, data: children };
    }
    return newItem;
  }

  function recursiveSetMapList(menu = [], mapList, level) {
    if (_.isArray(menu)) {
      menu.forEach((item) => {
        if (item.href && item.showSideNav !== false && !item.data?.length) {
          // 节点包含href跳转
          menuNav[item.href] = mapList;
        }
        if (_.isArray(item.data) && item.data.length > 0) {
          recursiveSetMapList(item.data, mapList, level + 1);
          const hasLeaf = item.data.filter((itemSub) => !itemSub.hide).some((itemSub) => itemSub.data.length);
          if (level >= 3 && !hasLeaf) {
            const mapList = (item.data || []).filter((item) => !item.hide);
            setLeafMapList(item.data, mapList);
          }
        } else if (item.singleTabNav) {
          setLeafMapList([item], [item]);
        }
      });
    }
  }

  function setLeafMapList(menu, mapList) {
    menu.forEach((item) => {
      let newMapList = mapList;
      if (item.href && item.singleTabNav && newMapList.length > 1) {
        newMapList = [item];
      }
      if (item.href && !item.hideTabNav) {
        // 节点包含href跳转
        leafNav[item.href] = newMapList;
      }
    });
  }
  return { menuNav, leafNav };
}

export default function buildSiteMap(loginStatus = {}) {
  const pathTreeMap = {}; // url对应的模块
  function filterGrant(item, _tree = [], _level = 0) {
    // label为function
    if (_.isFunction(item.label)) {
      item.label = item.label(loginStatus);
    }

    const level = _level + 1;
    const tree = level !== 2 ? [..._tree, item.label] : [..._tree];

    // 开启MP融合，不展示MP页卡
    if (item.mergeMpKey && loginStatus.feature_flag[item.mergeMpKey] === 1) {
      return null;
    }

    if (item.disabled && item.disabled(loginStatus)) {
      return null;
    }

    // 销售无权限
    if (item.perm && !item.perm(loginStatus)) {
      return null;
    }

    // 运营开关未开启，无权限
    if (!isOa() && item.enableKey && loginStatus.feature_flag[item.enableKey] !== 1) {
      return null;
    }

    // 运营无权限
    if (!_.isNil(item.purview) && loginStatus.privilege.indexOf(Number(item.purview)) === -1) {
      if (item.data) {
        // 存在有权限的子节点
        const children = _.compact(_.map(item.data, (child) => filterGrant(child, tree, level)));
        if (children && children.length) {
          return {
            ...item,
            data: children,
            tree,
          };
        }
      }
      return null;
    }

    // 用户未绑定相关平台账户
    if (_.isFunction(item.platform)) {
      item.platform = item.platform(loginStatus);
    }
    if (item.platform) {
      // 支持多个平台（有一个即可）
      if (_.isArray(item.platform)) {
        const flag = _.reduce(
          item.platform,
          (result, p) => {
            result = result || loginStatus.hasPlatform?.[p];
            return result;
          },
          false,
        );
        if (!flag) {
          return null;
        }
      } else if (!loginStatus.hasPlatform[item.platform]) {
        return null;
      }
    }

    const children = _.compact(_.map(item.data || [], (child) => filterGrant(child, tree, level)));

    if (item.data?.length && !children.length) {
      // 默认data.length非空，但过滤之后的为空，说明子菜单都无权限，可隐藏显示
      return null;
    }

    if (_.isFunction(item.href)) {
      const itemHref = item.href;
      item.href = itemHref(loginStatus);
    }

    // 检测 URL 是否匹配
    const match = (url) => {
      if (item.path) {
        const match = pathToRegexp.match(item.path, {
          decode: decodeURIComponent,
        });
        return Boolean(match(url));
      }

      if (item.href) {
        if (item.href === url) {
          return true;
        }
        const regex = new RegExp(`^${item.href.replace(/(\/:\w+)/g, '/(\\d+)')}`);
        return regex.test(url);
      }

      return false;
    };

    // 记录所属模块
    if (item.path) {
      pathTreeMap[item.path] = tree;
    }

    // 父节点href默认第一个子节点的href
    if (item.path && children.length > 0 && children[0].href) {
      return {
        match,
        ...item,
        href: children[0].href,
        data: children,
        tree,
      };
    }

    // 记录叶子结点所属模块，用于数据上报
    if (item.href && (!item.data?.length || !item.path)) {
      pathTreeMap[item.href] = level !== 2 ? tree : [...tree, item.label];
    }

    return {
      match,
      ...item,
      data: children,
      tree,
    };
  }

  function filterMenu(item) {
    // disabled 表示菜单禁用
    if (item.disabled && item.disabled(loginStatus)) {
      return null;
    }

    // hide 表示菜单是否可见
    if (item.hide) {
      return null;
    }

    const children = _.compact(_.map(item.data, (child) => filterMenu(child)));

    // 自身节点不渲染，且没有子路由，则不应该作为导航 （除了外跳的链接，如"知识库"）
    if (!children.length && !item.target === '_blank' && !(item.component || item.isMicroApp)) {
      return null;
    }

    // 父节点href默认第一个子节点的href
    if (item.path && children.length > 0 && children[0].href) {
      return {
        ...item,
        href: children[0].href,
        data: children,
      };
    }

    return {
      ...item,
      data: children,
    };
  }

  /**
   * @description 将单个菜单拍平
   * @param item
   * @param level
   * @returns {Array|*}
   */
  function flatRoute(item, level) {
    item = _.assign({ level }, item);
    if (_.isEmpty(item.data)) {
      return item;
    }
    return _.concat(
      item,
      _.flatMap(item.data, (child) => flatRoute(child, level + 1)),
    );
  }

  function getFirstMenuItem(navMenus = []) {
    if (navMenus[0] && navMenus[0].data && navMenus[0].data.length) {
      return getFirstMenuItem(navMenus[0].data);
    }
    return navMenus[0]?.href;
  }

  const { extraMenuMap = {} } = loginStatus;
  const getMenus = (menus) => {
    return menus.map((v) => {
      const { key } = v;
      const extra = extraMenuMap[key];
      let item = v;
      if (_.isObject(extra)) {
        item = {
          ...v,
          ...extra,
        };
      }
      if (_.isArray(item.data)) {
        return {
          ...item,
          data: getMenus(item.data),
        };
      }
      return item;
    });
  };

  let siteMap = getMenus(menus);

  let redirectUrl = '';

  if (isOa() && !loginStatus.saleStatus.agencyId) {
    // OA无系统管理权限，展示首页
    siteMap = menus.filter((item) => item.key === 'sysadmin');
    if (_.compact(_.map(siteMap, (item) => filterGrant(item))).length === 0) {
      redirectUrl = '/homepage';
    }
  }

  // grantMenu: 过滤权限之后的菜单
  const grantMenu = _.compact(_.map(siteMap, (item) => filterGrant(item)));
  // navMenu: 过滤权限和不可见之后的菜单，用于菜单的直接渲染
  const navMenu = _.compact(_.map(grantMenu, (item) => filterMenu(item)));
  const routes = _.orderBy(
    _.uniqBy(
      _.filter(
        _.flatMap(grantMenu, (item) => flatRoute(item, 0)),
        (item) => item.href && (item.component || item.isMicroApp),
      ),
      'href',
    ),
    ['level', 'href'],
    ['desc', 'desc'],
  );

  const pathTabNavMap = buildPathTabNavMap(grantMenu);
  const pathSubNavMap = buildPathSubNavMap(grantMenu);
  const { menuNav: pathMenuNavMap, leafNav: pathLeafNavMap } = buildPathMenuNavMap(grantMenu);


  // 构造返回结果
  return {
    navMenu,
    redirectUrl: redirectUrl || getFirstMenuItem(navMenu),
    routes,
    pathTabNavMap,
    pathSubNavMap,
    pathMenuNavMap,
    pathLeafNavMap,
    pathTreeMap,
  };
}
